/**
 * Yard schedule import — the client side of the chunked apply.
 *
 * The server applies a bounded slice of the import per call (about 30 write
 * operations, built to finish in well under 15 s) and answers
 * `committed_partial` with `counts.remaining > 0` until nothing is left. This
 * loop calls it again until it is done and reports progress ("Applying… N of
 * M") after every pass.
 *
 * Never reports a timeout while the server is still progressing:
 *  - each call is bounded by the API client (45 s);
 *  - a pass that times out (or gets a gateway/transient failure) is retried
 *    ONCE. A retry is safe because commit is idempotent: the server re-derives
 *    the diff from current state, and every create is keyed on a unique
 *    ScheduleRef/ItpRef, so a pass that did land is seen as already applied;
 *  - if the retry also fails the loop stops with a "taking longer than
 *    expected, press Apply to continue" outcome — not an "Apply failed:
 *    timeout" — because the writes already made are kept and a further Apply
 *    resumes from there.
 * A real error count from the server still stops the loop and is reported.
 */

const RETRYABLE_STATUS = new Set([502, 503, 504]);
const RETRYABLE_CODES = new Set(["timeout", "network_error"]);
const WRITE_KEYS = ["created", "updated", "retired", "cancelled", "vanishedCancelled", "reinstated"];

export function isRetryablePassFailure(result) {
  if (!result) return true;
  if (result.status === 0) {
    const code = result.body && result.body.errorCode;
    return !code || RETRYABLE_CODES.has(code);
  }
  return RETRYABLE_STATUS.has(result.status);
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Write operations the server executed in one pass (its own `ops`, else the sum of the write buckets). */
export function opsInPass(counts) {
  if (!counts) return 0;
  if (counts.ops !== undefined) return num(counts.ops);
  return num(counts.created) + num(counts.updated) + num(counts.retired) + num(counts.cancelled) + num(counts.vanishedCancelled) + num(counts.reinstated) + num(counts.error);
}

/**
 * @param {object} args
 * @param {() => Promise<{ok:boolean,status:number,body:object|null}>} args.commitPass one call to the commit route
 * @param {(p:{done:number,total:number,pass:number}) => void} [args.onProgress]
 * @param {number} [args.maxPasses]
 * @returns {Promise<{outcome:"committed"|"error"|"stalled"|"incomplete", body?:object, counts?:object, message?:string, done:number, total:number}>}
 */
export async function runImportCommit({ commitPass, onProgress, maxPasses = 400 }) {
  let total = 0;
  let done = 0;
  let idlePasses = 0;
  const written = {};
  for (let pass = 1; pass <= maxPasses; pass += 1) {
    let result = await commitPass();
    if (isRetryablePassFailure(result) && !(result && result.ok)) {
      // One retry of the same pass; commit is idempotent so this cannot double-apply.
      result = await commitPass();
      if (isRetryablePassFailure(result) && !(result && result.ok)) {
        return {
          outcome: "stalled",
          message: "Applying is taking longer than expected. What has been applied is kept — press Apply to continue.",
          done,
          total,
        };
      }
    }
    const body = (result && result.body) || {};
    const counts = body.counts || {};
    const errorCount = num(counts.error);
    if (!result.ok || errorCount > 0) {
      const reason = body.errorCode || body.message || `http_${result.status}`;
      const errText = errorCount ? ` — ${errorCount} item(s) failed to write` : "";
      return { outcome: "error", message: `Apply failed: ${reason}${errText}`, body, done, total };
    }
    const remaining = num(counts.remaining);
    const ops = opsInPass(counts);
    // `remaining` is the server's own count, so progress stays correct even if
    // an earlier pass was lost to a timeout and re-run.
    total = Math.max(total, done + ops + remaining);
    done = Math.max(done, total - remaining);
    for (const key of WRITE_KEYS) if (counts[key] !== undefined) written[key] = (written[key] || 0) + num(counts[key]);
    if (onProgress) onProgress({ done, total, pass });
    if (body.status === "committed" && remaining === 0) {
      // Each pass reports only what IT wrote (earlier passes' rows read as
      // unchanged on the next pass), so the closing summary adds the passes up.
      const summary = { ...written };
      for (const key of ["conflict", "invalid", "error"]) if (counts[key] !== undefined) summary[key] = num(counts[key]);
      return { outcome: "committed", body, counts: summary, done: total, total };
    }
    idlePasses = ops === 0 && remaining > 0 ? idlePasses + 1 : 0;
    if (idlePasses >= 3) {
      return {
        outcome: "incomplete",
        message: "Apply made no progress — press Apply again to continue.",
        body,
        done,
        total,
      };
    }
  }
  return {
    outcome: "incomplete",
    message: "Still applying after many passes — press Apply again to continue.",
    done,
    total,
  };
}
